import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Send } from 'lucide-react';
import { fetchGroupChatWeeklyReport, runGroupChatWeeklyReport } from '../../services/api';

/** Admin panel — Group Chat weekly digest tab. */
export default function GroupChatReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    fetchGroupChatWeeklyReport()
      .then((r) => { setReport(r); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const regen = async (email) => {
    setRunning(true); setErr(null);
    try {
      const r = await runGroupChatWeeklyReport(email);
      setReport(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <p style={{ color: '#5C6B73' }}>Loading weekly report…</p>;
  if (err) return <p style={{ color: '#EF5350' }}>{err}</p>;
  if (!report) return <p style={{ color: '#5C6B73' }}>No report yet.</p>;

  const totals = report.totals || {};
  const byTopic = report.by_topic || [];
  const keywords = report.top_keywords || [];
  const labels = report.moderation_labels || [];

  return (
    <div data-testid="admin-groupchat-tab" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#2F3E46', margin: 0, fontFamily: 'Outfit, sans-serif' }}>Group Chat — Weekly Digest</h2>
          <div style={{ fontSize: 12, color: '#5C6B73', marginTop: 4 }}>
            Period: <strong>{(report.period_start || '').slice(0,10)} → {(report.period_end || '').slice(0,10)}</strong> · generated {(report.generated_at || '').slice(0,16).replace('T',' ')} UTC
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="regen-report-btn" onClick={() => regen(false)} disabled={running} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #6B8E23', backgroundColor: '#FFF', color: '#6B8E23', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            {running ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />} Regenerate
          </button>
          <button data-testid="email-report-btn" onClick={() => regen(true)} disabled={running} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', backgroundColor: '#6B8E23', color: '#FFF', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Send size={14} /> Email Admins Now
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
        {[
          ['Messages', totals.messages, '#EDF1E5', '#4a6e18'],
          ['Approved', totals.approved, '#E8F5E9', '#2E7D32'],
          ['Redacted', totals.redacted, '#FFF3E0', '#A65500'],
          ['Blocked',  totals.blocked,  '#FFEBEE', '#C62828'],
        ].map(([label, val, bg, color]) => (
          <div key={label} style={{ backgroundColor: bg, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#2F3E46', marginTop: 4 }}>{val || 0}</div>
          </div>
        ))}
      </div>

      <div style={{ backgroundColor: '#FFF', borderRadius: 14, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#2F3E46', margin: '0 0 8px', fontFamily: 'Outfit, sans-serif' }}>By Topic</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#F7F7F3', textAlign: 'left' }}>
            <th style={{ padding: '6px 8px' }}>Topic</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Approved</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Redacted</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Blocked</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sentiment</th>
          </tr></thead>
          <tbody>
            {byTopic.length === 0 && <tr><td colSpan="6" style={{ padding: 14, textAlign: 'center', color: '#999' }}>No messages this week.</td></tr>}
            {byTopic.map((t, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #EEE' }}>
                <td style={{ padding: '6px 8px' }}>{t.topic}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{t.messages}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#2E7D32' }}>{t.approved}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#A65500' }}>{t.redacted}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#C62828' }}>{t.blocked}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(Math.round((t.avg_sentiment || 0) * 100) / 100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ backgroundColor: '#FFF', borderRadius: 14, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#2F3E46', margin: '0 0 8px', fontFamily: 'Outfit, sans-serif' }}>Top Keywords</h3>
        {keywords.length === 0 ? <em style={{ color: '#999', fontSize: 13 }}>No indexed keywords yet.</em> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywords.map((k, i) => (
              <span key={i} style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 9999, backgroundColor: '#EDF1E5', color: '#4a6e18', fontSize: 12, fontWeight: 600 }}>{k.keyword} · {k.count}</span>
            ))}
          </div>
        )}
      </div>

      <div style={{ backgroundColor: '#FFF', borderRadius: 14, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#2F3E46', margin: '0 0 8px', fontFamily: 'Outfit, sans-serif' }}>Moderation Activity</h3>
        {labels.length === 0 ? <em style={{ color: '#999', fontSize: 13 }}>No moderation actions this week.</em> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {labels.map((l, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #EEE' }}>
                  <td style={{ padding: '6px 8px' }}>{l.label}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{l.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 11, color: '#999' }}>
        Auto-runs every Sunday 09:00 UTC and emails admins. Click <strong>Email Admins Now</strong> to fire off a fresh digest immediately.
      </p>
    </div>
  );
}
